/**
 * SC-257 × SC-209: a way to ask the LiveKit connector to resume remote audio from somewhere that is not the
 * main document — a Document Picture-in-Picture window, in particular.
 *
 * SC-257 already resumes on `pointerdown` / `keydown` / `touchstart`, but on *this* document. A click inside a
 * PiP window is dispatched in the PiP document, so those listeners never hear it, and with the window open the
 * tab never becomes visible again either. Nothing could un-block audio from inside the window.
 *
 * The gesture itself does reach the opener: Chrome 126+ propagates user activation between a Document PiP
 * window and its opener (both ways, one budget), so `room.startAudio()` called from the PiP click's handler is
 * allowed. It only has to be asked for — and the connector, which owns the Room and the resume routine, sits far
 * above anything that renders into the window. The other toolbar signals already travel as `CustomEvent`s on
 * `window` (`livekit-video-state-changed`, `livekit-retry-media-publish`), so this uses the same seam.
 */

export const LIVEKIT_AUDIO_RESUME_REQUESTED = 'livekit-audio-resume-requested'

export interface AudioResumeRequestDetail {
  /** Recorded on the resume analytics event, so a PiP-originated recovery is distinguishable from a banner click. */
  source: string
  /**
   * Resume even though `Room.canPlaybackAudio` still reads true.
   *
   * That flag only tracks whether an audio *element* was allowed to start: livekit-client writes it in
   * `handleAudioPlaybackStarted`/`handleAudioPlaybackFailed` and when it acquires a context, and it registers no
   * `statechange` listener on the AudioContext at all. So the failure SC-257 is actually about — the browser
   * suspending the shared `webAudioMix` context behind a backgrounded tab — leaves `canPlaybackAudio` true and
   * every gate keyed on it shut. That is exactly the state a PiP window is open in, which would make the
   * seam above useless in the one case it was built for. A request that comes from a real user gesture
   * therefore says so, and the connector calls `startAudio()` regardless; it is idempotent, and it resumes a
   * suspended context on the way through.
   */
  force?: boolean
}

/** Fallback attribution for a request dispatched without a detail, e.g. from a console or a bare `Event`. */
export const AUDIO_RESUME_REQUEST_DEFAULT_SOURCE = 'requested'

/**
 * Asks the connector to run its SC-257 resume routine now. Safe to repeat: the routine does nothing when the room
 * is not connected, and on a room that reports itself playing a forced request runs at most once per
 * `FORCED_AUDIO_RESUME_COOLDOWN_MS`, because each run rebuilds the Web Audio graph. Call it from the click handler
 * itself, not from a later tick, so the browser still counts the gesture.
 */
export function requestLiveKitAudioResume(
  source: string,
  target: EventTarget = window,
  opts?: { force?: boolean }
): void {
  target.dispatchEvent(
    new CustomEvent<AudioResumeRequestDetail>(LIVEKIT_AUDIO_RESUME_REQUESTED, {
      detail: { source, force: opts?.force === true },
    })
  )
}

/** Whether this request asked to bypass the `canPlaybackAudio` gate. Absent or malformed reads as `false`. */
export function readAudioResumeRequestForce(event: Event): boolean {
  const detail = (event as Partial<CustomEvent<Partial<AudioResumeRequestDetail>>>).detail
  return detail?.force === true
}

/** The `source` a request carried, or the default when it carried none — a request never fails for want of a label. */
export function readAudioResumeRequestSource(event: Event): string {
  const detail = (event as Partial<CustomEvent<Partial<AudioResumeRequestDetail>>>).detail
  const source = detail?.source
  return typeof source === 'string' && source.length > 0 ? source : AUDIO_RESUME_REQUEST_DEFAULT_SOURCE
}
